/**
 * One run's ranking, as the table a reader scans and a drawer they open.
 *
 * The redesign is a division of labour. The table answers *which rows are worth
 * opening* — rank, name, how much of the rubric was measured, tier, Elo — in one line
 * per row, so ten rows can be compared without scrolling. Everything that needs a
 * measure of its own (the rationale paragraph, the evidence list, the ligand records,
 * the report action) moves into `Drawer`. That retires the `colSpan={9}` the expanded
 * row used to carry, which was bookkeeping every column change had to chase and which
 * went silently wrong rather than failing.
 *
 * ── The one thing this table is for ─────────────────────────────────────────────
 *
 * `AxisSpine` in the third column. The ranking holds every review survivor, so a
 * target with all six rubric axes measured and one with two sit adjacent in rank
 * order, and in the old table they were indistinguishable — both a name, a score and a
 * tier. The spine makes the difference the *texture* of the column: dense rows are
 * well-evidenced, gappy rows are speculative, and a hollow segment says which axis is
 * missing. Nothing else on this page carries that.
 *
 * ── Rules kept from the old file ────────────────────────────────────────────────
 *
 * - **Sorting is the API's**, because the ranking is paged; a header only offers a
 *   column `SORT_COLUMNS` accepts, and only one whose value is on screen.
 * - **The selection is pruned to the loaded rows** on every poll, re-sort and page
 *   turn, returning `prev` unchanged when nothing was pruned.
 * - **The CSV is wider than the table** and exports `score` with `coverage`, the
 *   review gaps and the rationale.
 * - **The report affordance is in the drawer, never a column.** It costs several
 *   dollars and fifteen minutes; a column of them is one mis-click away from that.
 *
 * ── Two things that are new and deliberate ──────────────────────────────────────
 *
 * **Group by pathway** is a toggle rather than a colour. Pathway is unbounded model
 * text, so hashing it to a palette assigns hues nobody can predict, reuses them across
 * unrelated names and lands some pathway on the red that means *fault* everywhere else
 * on this page (see `Chip`'s own note). What a reader wants — every fatty-acid target
 * together — is grouping, so the chip stays quiet and this does the work. It groups the
 * **loaded page** only, and its counts say so, because the rows it can see are the only
 * rows it has.
 *
 * **The rows cascade in once.** `firstPaint` is a ref read during render and cleared in
 * an effect, not state: a state update immediately after mount would re-render and
 * cancel the `animate-rise` it had just started. Re-sorting and paging reuse the same
 * `key={t.id}` rows, so nothing replays — which is the point. A ranking that re-animates
 * on every sort turns a read into a wait.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Layers, MoreHorizontal } from 'lucide-react'
import {
  cn,
  formatEvidenceList,
  formatLigandStarts,
  formatNumber,
} from '../lib/utils'
import { joinList, toCsv, type CsvColumn } from '../lib/csv'
import { downloadCsv } from '../lib/download'
import { CompoundId } from './CompoundId'
import { EvidenceLinks } from './EvidenceLinks'
import { ReportButton } from './ReportButton'
import { TierBadge } from './StatusBadge'
import { AxisSpine, SPINE_AXES } from './ui/AxisSpine'
import { Badge } from './ui/Badge'
import { Button } from './ui/Button'
import { Card, CardHeader } from './ui/Card'
import { Chip } from './ui/Chip'
import { Drawer, DrawerSection } from './ui/Drawer'
import { DownloadCsvButton } from './ui/DownloadCsvButton'
import { ScoreMeter } from './ui/Meter'
import { EmptyState } from './ui/States'
import type { LigandStart, SortColumn, SortDirection, Target } from '../api/types'
import type { ReportsState } from '../hooks/useReports'

interface Column {
  /** `null` = not sortable. The API 400s on any column outside `SORT_COLUMNS`. */
  key: SortColumn | null
  label: string
  /**
   * A second sort offered from the same header cell, for a value shown on the
   * cell's subline. `pathway` lives under the protein name, so it is sortable from
   * the protein header rather than costing the table a column of its own.
   */
  also?: { key: SortColumn; label: string }
  className?: string
  title?: string
}

const COLUMNS: Column[] = [
  { key: 'rank', label: '#', className: 'w-12 text-right' },
  {
    key: 'protein',
    label: 'Protein',
    also: { key: 'pathway', label: 'pathway' },
  },
  {
    key: null,
    label: 'Rubric axes',
    className: 'w-44',
    // Named from the rubric, so this label cannot describe a weighting the spine
    // is not drawing.
    title: `One segment per rubric axis, width in proportion to its weight: ${SPINE_AXES.map(
      (a) => `${a.label} ${a.weight}`,
    ).join(', ')}. Fill height is the axis score; a hollow dashed segment was never measured.`,
  },
  { key: null, label: 'Score · coverage', className: 'w-52' },
  { key: 'tier', label: 'Tier', className: 'w-28' },
  { key: 'elo', label: 'Elo', className: 'w-16 text-right' },
  { key: null, label: '', className: 'w-12' },
]

/** Selection + disclosure columns, so no subheader hardcodes a span. */
const COLSPAN = COLUMNS.length + 2

/**
 * What a CSV export of one run's ranking holds.
 *
 * Wider than the table on purpose: the columns on screen are what fits beside a
 * readable protein name, this is the row. Four things are here that the table
 * does not show and that a spreadsheet is exactly the place for:
 *
 * - **`score` and `coverage` together** — the rubric's own result pair
 *   (context/target-criteria §4/§7, stored by infra/sql/012). Neither is exported
 *   without the other: a score alone reads as a stronger measurement than it is.
 * - **`review_gaps`** — the actionable half of an `incomplete` verdict. On screen
 *   they are in the drawer, so a reader exporting a hundred rows would otherwise
 *   have to open a hundred of them to see which fields to go and fill.
 * - **`rationale`** — the paragraph the table has no column wide enough for.
 * - **`btl_score`** — which the redesigned table no longer shows, because the
 *   pairwise fit is a statement about *this* tournament and is rendered as one, in
 *   `LeaderBand`. The number itself still has to leave the page.
 *
 * `axis_scores` is deliberately absent; see the field's own note in `api/types.ts`.
 */
const CSV_COLUMNS: CsvColumn<Target>[] = [
  { header: 'rank', value: (t) => t.rank },
  { header: 'protein', value: (t) => t.protein },
  { header: 'accession', value: (t) => t.accession },
  { header: 'pathway', value: (t) => t.pathway },
  { header: 'moa', value: (t) => t.moa },
  { header: 'elo', value: (t) => t.elo },
  { header: 'btl_score', value: (t) => t.btl_score },
  { header: 'score', value: (t) => t.score },
  // The 0–1 fraction as stored, not a rendered percentage: a spreadsheet can
  // format it, and re-deriving 0.75 from "75%" is lossy in the direction that
  // inflates it.
  { header: 'coverage', value: (t) => t.coverage },
  { header: 'tier', value: (t) => t.tier },
  { header: 'review_verdict', value: (t) => t.review_verdict },
  { header: 'review_gaps', value: (t) => joinList(t.review_gaps) },
  { header: 'scope_flag', value: (t) => t.scope_flag },
  // Recorded, not scored — a spreadsheet is where this column ends up beside
  // `score` with nothing to say they are different kinds of number, so the header
  // says it. The source travels with the count because a blank count means the
  // search did not run and only the source says why; `0` means it ran and found
  // nothing orderable. Same three columns and same flattening as the board export.
  {
    header: 'ligand_start_count (orderable analogs found — recorded, not scored)',
    value: (t) => t.ligand_start_count,
  },
  {
    header: 'ligand_start_source (blank count = not measured; see this column for why)',
    value: (t) => t.ligand_start_source,
  },
  {
    header: 'ligand_starts (pdb[provenance] comp_id source:catalog_id@similarity)',
    value: (t) => joinList(formatLigandStarts(t.ligand_starts)),
  },
  { header: 'evidence', value: (t) => joinList(formatEvidenceList(t.evidence)) },
  { header: 'rationale', value: (t) => t.rationale },
  // The identity a reader can take back to the harness: the hypothesis as the run
  // named it, the run, and the row id a report is filed against.
  { header: 'hypothesis_id', value: (t) => t.hypothesis_id },
  { header: 'run_id', value: (t) => t.run_id },
  { header: 'target_id', value: (t) => t.id },
  { header: 'created_at', value: (t) => t.created_at },
]

interface Props {
  targets: Target[]
  sort: SortColumn
  direction: SortDirection
  /** Sorting is done by the API, not in the browser: the ranking is paged. */
  onSort: (column: SortColumn) => void
  /**
   * Per-target report state. Passed whole rather than as six props because the
   * affordance needs the row, the in-flight ids and both callbacks, and threading
   * those separately makes the call site unreadable.
   *
   * The hook is scoped to this run, so no run id is passed on to `ReportButton`
   * here — unlike `BoardTable`, whose rows are cross-run and must name one.
   */
  reports: ReportsState
  /** The signed-in address, shown in the confirmation. Empty when auth is off. */
  accountEmail: string
}

export function TargetTable({
  targets,
  sort,
  direction,
  onSort,
  reports,
  accountEmail,
}: Props) {
  // One row's detail at a time, in a drawer. The ranking stays on screen beside it,
  // which the old expanded row could not do — it pushed the rows below off the
  // screen, so comparing a target with the one under it meant closing the thing you
  // were reading.
  const [openId, setOpenId] = useState<number | null>(null)

  // Which rows a CSV export will hold, as target ids. Local to this table for the
  // same reason it always was: the board has its own selection over different row
  // identities, and one shared set would let a click on one tab change what the
  // other exports.
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const [grouped, setGrouped] = useState(false)

  const toggleSelected = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // Prune the selection to the rows actually on screen, on every change of `targets`
  // — a poll, a re-sort or a page turn. Without this, a selection outlives the rows
  // it named: the count in the button keeps saying twelve while the export, built
  // from the current rows, silently contains fewer, and a reader has no way to see
  // which reading is right. Paging therefore clears the selection, which is honest:
  // the CSV is built from rows in memory, so a selection spanning pages would
  // promise rows the file cannot contain.
  //
  // Returning `prev` unchanged when nothing was pruned is load-bearing: React bails
  // out on an identical state value, and a fresh `Set` every time would re-render
  // this effect's own dependency forever.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev
      const live = new Set(targets.map((t) => t.id))
      const next = new Set([...prev].filter((id) => live.has(id)))
      return next.size === prev.size ? prev : next
    })
    // The open drawer names a row by id, so a page turn under it would leave a panel
    // captioned with a row that is no longer in the table.
    setOpenId((prev) =>
      prev !== null && !targets.some((t) => t.id === prev) ? null : prev,
    )
  }, [targets])

  // One cascade, on the first paint that had rows. A ref rather than state, because a
  // state update right after mount re-renders and cancels the animation it started.
  const firstPaint = useRef(true)
  const cascade = firstPaint.current && targets.length > 0
  useEffect(() => {
    if (targets.length > 0) firstPaint.current = false
  }, [targets])

  const selectedRows = targets.filter((t) => selected.has(t.id))
  const allSelected = targets.length > 0 && selectedRows.length === targets.length

  /**
   * The loaded page grouped by pathway, in order of first appearance.
   *
   * First appearance rather than alphabetical, so the group order still follows the
   * sort a reader chose: with the default rank sort, the pathway of the leading
   * target is the first group. A `null` pathway gets a named group of its own rather
   * than being dropped or folded in with an empty label — no pathway recorded is a
   * fact about the row.
   */
  const groups = useMemo(() => {
    if (!grouped) return null
    const order: (string | null)[] = []
    const byPathway = new Map<string | null, Target[]>()
    for (const t of targets) {
      const key = t.pathway ?? null
      if (!byPathway.has(key)) {
        byPathway.set(key, [])
        order.push(key)
      }
      byPathway.get(key)!.push(t)
    }
    return order.map((key) => ({ key, rows: byPathway.get(key)! }))
  }, [grouped, targets])

  const download = () => {
    // Everything when nothing is selected, per the button's own label.
    const rows = selectedRows.length > 0 ? selectedRows : targets
    // Named after the run rather than the file's contents: two exports of the same
    // run land as `…(1).csv`, which is the browser's job, whereas a name that does
    // not say which run this is makes the file unusable a week later. Read off the
    // rows, so it cannot name a run other than the one exported.
    downloadCsv(`targets-${rows[0]?.run_id ?? 'export'}.csv`, toCsv(rows, CSV_COLUMNS))
  }

  const openTarget = openId === null ? null : targets.find((t) => t.id === openId)

  // A running index across the groups, so the cascade reads as one list filling in
  // rather than restarting at every pathway.
  let paintIndex = 0
  const row = (t: Target) => {
    const delay = cascade ? paintIndex++ * 15 : 0
    return (
      <TargetRow
        key={t.id}
        target={t}
        selected={selected.has(t.id)}
        onToggleSelected={toggleSelected}
        onOpen={setOpenId}
        cascade={cascade}
        delay={delay}
      />
    )
  }

  return (
    <>
      <Card
        // `Card` clips its children, which is right for a table reaching the card's
        // edges and wrong for the sticky pathway subheaders below — a `position:
        // sticky` element resolves against its nearest scrolling ancestor and an
        // `overflow: hidden` one silently stops it sticking at all. `twMerge` resolves
        // the conflict in this class's favour, so the header stays put and the
        // subheaders stick against the page's own scroll container.
        className="animate-rise overflow-visible"
      >
        <CardHeader
          eyebrow="Ranked targets"
          title="This run's ranking"
          description="Every hypothesis that survived review, in the order the tournament put them. A row's spine shows how much of the rubric was actually measured — two rows with the same score are not the same finding if one of them was scored on two axes."
          actions={
            <>
              <Button
                variant={grouped ? 'outline' : 'ghost'}
                size="sm"
                onClick={() => setGrouped((g) => !g)}
                aria-pressed={grouped}
                title="Insert a subheader per pathway. It groups the rows on this page only — the ranking is paged server-side, so a pathway can continue on the next one. Sort by pathway to keep a group together across pages."
              >
                <Layers className="h-3.5 w-3.5" />
                {grouped ? 'Ungroup' : 'Group by pathway'}
              </Button>
              <DownloadCsvButton
                selectedCount={selectedRows.length}
                rowCount={targets.length}
                noun="targets"
                onDownload={download}
              />
            </>
          }
        />

        {targets.length === 0 ? (
          <EmptyState
            message="This run has no ranked targets."
            hint="The report-generator writes the ranking at the end of a sweep, so a run still in the tournament has none yet. A finished sweep with none ranked is a result: nothing survived review. What it ruled out is below."
          />
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="w-8 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() =>
                      setSelected(
                        allSelected ? new Set() : new Set(targets.map((t) => t.id)),
                      )
                    }
                    // A ref callback, not a `useEffect`: `indeterminate` is a DOM
                    // property with no React prop, and the callback runs on every
                    // render, so the box cannot fall out of step with the state the way
                    // an effect with a dependency list can. The braces matter — a ref
                    // callback must not return a value.
                    ref={(el) => {
                      if (el) el.indeterminate = selectedRows.length > 0 && !allSelected
                    }}
                    aria-label={
                      allSelected
                        ? 'Clear the selection'
                        : 'Select every target on this page'
                    }
                    className="h-3.5 w-3.5 accent-brand-500"
                  />
                </th>
                {COLUMNS.map((col) => (
                  <th
                    key={col.label || 'actions'}
                    title={col.title}
                    className={cn(
                      'px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500',
                      col.className,
                    )}
                  >
                    <span className="inline-flex items-baseline gap-2">
                      {col.key ? (
                        <SortHeader
                          label={col.label}
                          column={col.key}
                          sort={sort}
                          direction={direction}
                          onSort={onSort}
                        />
                      ) : (
                        col.label
                      )}
                      {col.also && (
                        <SortHeader
                          label={col.also.label}
                          column={col.also.key}
                          sort={sort}
                          direction={direction}
                          onSort={onSort}
                          className="normal-case tracking-normal text-slate-600"
                        />
                      )}
                    </span>
                  </th>
                ))}
                <th className="w-10 px-3 py-2">
                  <span className="sr-only">Open the target</span>
                </th>
              </tr>
            </thead>

            {groups ? (
              // One `tbody` per pathway. A grouped table with a single body would put
              // the subheader in the same row stream as the data, which a screen
              // reader reads as a malformed row; a body per group is what makes the
              // subheader a caption for the rows under it.
              groups.map((group) => (
                <tbody key={group.key ?? ' no-pathway'}>
                  <tr>
                    <td
                      colSpan={COLSPAN}
                      className="sticky top-0 z-10 border-y border-slate-800 bg-slate-800/90 px-3 py-1.5 backdrop-blur"
                    >
                      <span className="font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-300">
                        {group.key ?? 'No pathway recorded'}
                      </span>
                      <span
                        className="ml-2 font-mono text-2xs tabular-nums text-slate-500"
                        title="Rows in this group on this page. The ranking is paged server-side, so it is not the run's total for this pathway."
                      >
                        {group.rows.length} on this page
                      </span>
                    </td>
                  </tr>
                  {group.rows.map(row)}
                </tbody>
              ))
            ) : (
              <tbody>{targets.map(row)}</tbody>
            )}
          </table>
        )}
      </Card>

      {/* The drawer is a sibling of the card, not a child of it: `Drawer` is a
          `fixed inset-0` overlay with no portal, and the card carries
          `animate-rise`, whose `both` fill-mode leaves `transform: translateY(0)`
          applied for good. A transform other than `none` makes the element the
          containing block for fixed-position descendants, so inside the card the
          overlay would have covered the table rather than the viewport — and it
          would have been trapped in the card’s stacking context too. Nothing in
          the type checker or the linter can see this. */}
      {openTarget && (
        <TargetDrawer
          target={openTarget}
          reports={reports}
          accountEmail={accountEmail}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  )
}

/** A column header that sorts. Split out because two of them share a cell. */
function SortHeader({
  label,
  column,
  sort,
  direction,
  onSort,
  className,
}: {
  label: string
  column: SortColumn
  sort: SortColumn
  direction: SortDirection
  onSort: (column: SortColumn) => void
  className?: string
}) {
  const active = sort === column
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      aria-label={`Sort by ${label}`}
      className={cn(
        'inline-flex items-center gap-1 hover:text-slate-200',
        active && 'text-slate-200',
        className,
      )}
    >
      {label}
      {active &&
        (direction === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        ))}
    </button>
  )
}

/**
 * One ranked target: a line, a quiet subline, and the two bars.
 *
 * Its own component so the cascade delay is a prop rather than a closure over a
 * mutable counter, and so the grouped and ungrouped bodies render byte-identical rows.
 */
function TargetRow({
  target: t,
  selected,
  onToggleSelected,
  onOpen,
  cascade,
  delay,
}: {
  target: Target
  selected: boolean
  onToggleSelected: (id: number) => void
  onOpen: (id: number) => void
  cascade: boolean
  delay: number
}) {
  return (
    <tr
      className={cn(
        'border-b border-slate-800/70 last:border-b-0 hover:bg-slate-800/40',
        cascade && 'animate-rise',
      )}
      style={cascade ? { animationDelay: `${delay}ms` } : undefined}
    >
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelected(t.id)}
          // Named, not "row 4": a screen reader announcing the box has to say which
          // target it selects, and the rank alone is meaningless once re-sorted.
          aria-label={`Select ${t.protein} for export`}
          className="h-3.5 w-3.5 accent-brand-500"
        />
      </td>

      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">
        {t.rank ?? '—'}
      </td>

      <td className="px-3 py-2">
        <button
          type="button"
          onClick={() => onOpen(t.id)}
          className="max-w-full truncate text-left font-medium text-slate-100 hover:text-brand-300"
        >
          {t.protein}
        </button>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-slate-500">
          {/* No accession is an answer about the row: nothing could be searched for
              it. Said, not left blank. */}
          <span className="font-mono">
            {t.accession ?? (
              <span className="font-sans italic">no accession</span>
            )}
          </span>
          {t.pathway && <Chip>{t.pathway}</Chip>}
          <Flags target={t} />
        </div>
      </td>

      <td className="px-3 py-2">
        <AxisSpine axisScores={t.axis_scores} />
      </td>

      <td className="px-3 py-2">
        <ScoreMeter score={t.score} coverage={t.coverage} />
      </td>

      <td className="px-3 py-2">
        <TierBadge tier={t.tier} />
      </td>

      <td
        className="px-3 py-2 text-right font-mono tabular-nums text-slate-200"
        title="Elo from this run's tournament, starting at 1500 with K=24. Unbounded, so it is never drawn as a bar."
      >
        {formatNumber(t.elo)}
      </td>

      <td className="px-3 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpen(t.id)}
          aria-label={`Open ${t.protein}`}
          title="Rationale, evidence, ligand starting points and the full report"
          className="h-7 w-7"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  )
}

/**
 * Why a ranked target is qualified the way it is, on the row rather than buried in
 * the rationale prose.
 *
 * Both badges exist because the ranking holds every review survivor instead of a
 * hand-cut top ten. A target with six axes measured and one with two sit in the same
 * table, in rank order, and looked identical.
 *
 * `incomplete` is amber, not red: it competed and placed, carrying gaps. A
 * `scope_flag` is likewise not a failure — out-of-scope target types are *penalised,
 * not filtered* (context/target-criteria §6), so the badge is what explains why an
 * otherwise strong target sits low.
 */
function Flags({ target }: { target: Target }) {
  if (!target.review_verdict && !target.scope_flag) return null
  return (
    <>
      {target.review_verdict && (
        <Badge
          variant="yellow"
          className="px-1.5 py-0 text-2xs"
          title="Too few review axes reached a verdict. It competed in the tournament carrying its evidence gaps."
        >
          {target.review_verdict}
        </Badge>
      )}
      {target.scope_flag && (
        <Badge
          variant="purple"
          className="px-1.5 py-0 text-2xs"
          title="Out of the program's preferred target scope. Penalised in the score, not filtered out."
        >
          {target.scope_flag}
        </Badge>
      )}
    </>
  )
}

/**
 * One target's detail: everything that needs a measure of its own.
 *
 * The order is the order a reader asks the questions in — what does the run claim,
 * how well was it scored, what is it, what backs it, what is missing, is there
 * anything to order — and the report, which is the only thing here that spends money,
 * is in the footer where it cannot be reached by scrolling past.
 */
function TargetDrawer({
  target: t,
  reports,
  accountEmail,
  onClose,
}: {
  target: Target
  reports: ReportsState
  accountEmail: string
  onClose: () => void
}) {
  return (
    <Drawer
      // "Ranked target" and not just the protein name: this run's ranking and the
      // cumulative board are different levels of record, and a panel that does not
      // say which one it came from invites reading a cross-run consolidation as one
      // run's result.
      eyebrow="Ranked target"
      title={t.protein}
      subtitle={
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono">{t.accession ?? 'No accession recorded'}</span>
          <span className="text-slate-500">
            rank {t.rank ?? '—'} of this run
          </span>
          <TierBadge tier={t.tier} />
        </span>
      }
      onClose={onClose}
      footer={
        <ReportButton
          targetId={t.id}
          protein={t.protein}
          accession={t.accession}
          report={reports.byTarget.get(t.id)}
          accountEmail={accountEmail}
          requesting={reports.requesting === t.id}
          downloading={
            reports.downloading !== null &&
            reports.downloading === reports.byTarget.get(t.id)?.id
          }
          error={reports.error?.targetId === t.id ? reports.error.message : null}
          onRequest={reports.request}
          onDownload={reports.download}
        />
      }
    >
      <DrawerSection label="Rationale">
        {/* whitespace-pre-wrap: the agent writes multi-paragraph rationales and
            collapsing them loses the structure. This is the measure the paragraph
            never had in a table cell. */}
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-300">
          {t.rationale ?? 'No rationale recorded.'}
        </p>
      </DrawerSection>

      <DrawerSection label="Rubric">
        <ScoreMeter score={t.score} coverage={t.coverage} />
        <div className="mt-3">
          <AxisSpine axisScores={t.axis_scores} className="max-w-xs" />
        </div>
        {/* The spine with its axes named. The table cell has hover titles; this is
            for the reader who cannot hover and for the one who wants the numbers.
            Axis names and order come from `SPINE_AXES`, so the rubric has one home. */}
        {t.axis_scores && (
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {SPINE_AXES.map((axis) => {
              const raw = t.axis_scores?.[axis.key]
              const measured = raw !== null && raw !== undefined
              return (
                <div
                  key={axis.key}
                  className="flex items-baseline justify-between gap-2 border-b border-slate-800/60 pb-0.5"
                >
                  <dt className="truncate text-slate-400">
                    {axis.label}{' '}
                    <span className="text-slate-600">·{axis.weight}</span>
                  </dt>
                  <dd
                    className={cn(
                      'shrink-0 font-mono tabular-nums',
                      measured ? 'text-slate-200' : 'font-sans text-2xs text-slate-500',
                    )}
                  >
                    {measured ? Math.round(raw) : 'not measured'}
                  </dd>
                </div>
              )
            })}
          </dl>
        )}
      </DrawerSection>

      {t.moa && (
        <DrawerSection label="Mode of action">
          <p className="text-sm text-slate-200">{t.moa}</p>
        </DrawerSection>
      )}

      {t.pathway && (
        <DrawerSection label="Pathway">
          <p className="text-sm text-slate-200">{t.pathway}</p>
        </DrawerSection>
      )}

      <DrawerSection label="Evidence">
        <EvidenceLinks evidence={t.evidence} />
      </DrawerSection>

      {/* The actionable half of an `incomplete` verdict: fields to go and fill, not a
          judgement on the target. Listed rather than folded into the prose so they
          read as a to-do. Rendered only when there is a verdict, because absence of a
          flag IS the pass and "no gaps" would be a verdict on nothing. */}
      {t.review_verdict && (
        <DrawerSection label="Unmeasured">
          {t.review_gaps?.length ? (
            <ul className="list-inside list-disc space-y-0.5 text-sm text-slate-300">
              {t.review_gaps.map((gap, i) => (
                <li key={i}>{gap}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">
              Review did not reach a verdict; no specific gap recorded.
            </p>
          )}
        </DrawerSection>
      )}

      {t.scope_flag && (
        <DrawerSection label="Scope">
          <p className="text-sm text-slate-300">
            Flagged <span className="font-mono">{t.scope_flag}</span> — out of the
            program's preferred target scope. Penalised in the score, not filtered
            out, so this is why an otherwise strong target can sit low.
          </p>
        </DrawerSection>
      )}

      <DrawerSection label="Ligand starting points">
        <LigandStarts target={t} />
      </DrawerSection>

      <DrawerSection label="Identity">
        <dl className="space-y-1.5 text-xs">
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-slate-500">Hypothesis</dt>
            <dd className="min-w-0 break-all font-mono text-slate-400">
              {t.hypothesis_id}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-slate-500">Run</dt>
            <dd className="min-w-0 break-all font-mono text-slate-400">{t.run_id}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-slate-500">Elo</dt>
            <dd className="font-mono tabular-nums text-slate-400">
              {formatNumber(t.elo)}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-slate-500">BTL</dt>
            <dd
              className="font-mono tabular-nums text-slate-400"
              title="The Bradley-Terry fit over this run's pairwise matches. Comparable inside this run only — a value from another run was fitted over a different pool with no shared opponent."
            >
              {formatNumber(t.btl_score, 4)}
            </dd>
          </div>
        </dl>
      </DrawerSection>
    </Drawer>
  )
}

/**
 * The ligand starting points, in four states that must not collapse into one.
 *
 * `infra/sql/020_ligand_starts.sql` stores three columns and `api/types.ts` states
 * the rule outright: render the three together or none of them.
 *
 * - **keys absent** — the row predates the migration. Nothing was backfilled.
 * - **`null` count** — the search did not run. `ligand_start_source` is the only
 *   thing that says why, so it is quoted rather than summarised.
 * - **`0`** — it ran and found nothing worth ordering an analog of. A *measurement*.
 * - **records** — the ligands, each with the provenance that decides what may be
 *   claimed from it.
 *
 * A cell that showed the middle two as the same dash has turned a finding into a gap,
 * and this is a column a chemist acts on.
 */
function LigandStarts({ target: t }: { target: Target }) {
  const recorded =
    'ligand_start_count' in t ||
    'ligand_start_source' in t ||
    'ligand_starts' in t

  if (!recorded) {
    return (
      <p className="text-sm text-slate-400">
        Not recorded — this row predates{' '}
        <span className="font-mono text-xs">infra/sql/020_ligand_starts.sql</span>,
        which backfilled nothing. It is not "none found": no search was ever
        attributed to this row.
      </p>
    )
  }

  const source = t.ligand_start_source
  const starts = t.ligand_starts

  if (t.ligand_start_count == null) {
    return (
      <p className="text-sm text-slate-400">
        Not measured — the sweep recorded no count.{' '}
        {source ? (
          <>
            The reason it stamped is{' '}
            <span className="font-mono text-xs text-amber-400">{source}</span>.
          </>
        ) : (
          'No reason was stamped either, so why it did not run is not established.'
        )}
      </p>
    )
  }

  if (!starts || starts.length === 0) {
    return (
      <p className="text-sm text-slate-300">
        Searched, and nothing was worth ordering an analog of — a result, not a gap.{' '}
        {source && (
          <>
            Stamped{' '}
            <span className="font-mono text-xs text-slate-400">{source}</span>.
          </>
        )}
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-2xs text-slate-500">
        {t.ligand_start_count} recorded, not scored — these are co-crystallised
        ligands and the catalog compounds similar to them. Similarity is not potency
        and a catalog id is not a CPD number.
      </p>
      {starts.map((s, i) => (
        <LigandStartCard key={`${s.pdb_id}-${s.comp_id}-${i}`} start={s} />
      ))}
    </div>
  )
}

/** One co-crystallised ligand, with the provenance that bounds what it can claim. */
function LigandStartCard({ start: s }: { start: LigandStart }) {
  const homolog = s.structure_provenance === 'homolog'
  const hits = s.catalog_hits
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-800/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Chip font="mono" emphasis>
          {s.pdb_id}
        </Chip>
        {/* The provenance and both numbers are rendered *with* the PDB id, not behind
            it: a structure of a different protein that cleared the identity and
            coverage floors does not establish this protein's pocket, and presenting
            it as the target's own structure is a claim nobody measured. */}
        {homolog ? (
          <Badge
            variant="yellow"
            className="px-1.5 py-0 text-2xs"
            title="A structure of a different protein that cleared the identity and coverage floors. It does not establish this protein's pocket."
          >
            homolog{s.structure_organism ? ` · ${s.structure_organism}` : ''}
          </Badge>
        ) : (
          <Badge
            variant="blue"
            className="px-1.5 py-0 text-2xs"
            title="A structure of this protein itself."
          >
            anchor
          </Badge>
        )}
        <span className="font-mono text-2xs tabular-nums text-slate-500">
          {Math.round(s.structure_identity * 100)}% identity ·{' '}
          {Math.round(s.structure_coverage * 100)}% coverage
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-baseline gap-x-2 text-xs">
        <span className="font-mono text-slate-200">{s.comp_id}</span>
        <span className="text-slate-400">{s.ligand_name ?? 'unnamed ligand'}</span>
        <span
          className="text-2xs text-slate-500"
          title={
            s.kept_because === 'subject_of_investigation'
              ? "RCSB's own curated annotation that this molecule is what the structure was solved to study."
              : "This repo's heavy-atom heuristic, not a curated annotation — a large buffer component can pass it."
          }
        >
          kept: {s.kept_because.replace(/_/g, ' ')}
        </span>
      </div>

      <p className="mt-1 break-all font-mono text-2xs text-slate-500">{s.smiles}</p>

      <div className="mt-2 border-t border-slate-800 pt-2">
        {hits === undefined || hits === null ? (
          // The ligand stands; the orderable half of the answer does not. Distinct
          // from an empty list, which is a search that ran.
          <p className="text-2xs text-amber-400">
            The catalog was not searched for this ligand, so whether anything
            orderable resembles it is unmeasured.
          </p>
        ) : hits.length === 0 ? (
          <p className="text-2xs text-slate-400">
            The catalog was searched and returned nothing similar — a result about
            the collection, not about the ligand.
          </p>
        ) : (
          <ul className="space-y-1">
            {hits.map((h, i) => (
              <li
                key={`${h.compound_id}-${i}`}
                className="flex flex-wrap items-baseline gap-2 text-2xs"
              >
                <CompoundId hit={h} />
                {h.source && <span className="text-slate-500">{h.source}</span>}
                <span className="font-mono tabular-nums text-slate-400">
                  {h.similarity != null ? h.similarity.toFixed(2) : 'no similarity'}
                </span>
                {h.tier && (
                  <span
                    className="text-slate-500"
                    title="A band over the similarity — exact ≥0.99, close ≥0.70, analog ≥0.50, weak below. Not a confidence and not potency."
                  >
                    {h.tier}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
